import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import ConfirmModal from "../components/ConfirmModal";
import RetailerReviewPanel from "../components/RetailerReviewPanel";
import { FaTrash, FaShoppingBasket, FaCheck, FaChevronDown, FaChevronUp, FaEnvelope, FaSms, FaCalendarAlt } from "react-icons/fa";
import { toast } from "../lib/toast";

const MEAL_PLAN_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const KROGER_CART_URL = "https://www.kroger.com/shopping/cart";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toAbsoluteUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function getRecipeThumbnail(recipe) {
  if (!recipe?.images?.length) return "";
  const firstImageMedia = recipe.images.find((media) => media?.image);
  return firstImageMedia?.image ? toAbsoluteUrl(firstImageMedia.image) : "";
}

export default function CartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("cart"); // "cart" | "history"
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shoppingList, setShoppingList] = useState(null);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasNext, setHistoryHasNext] = useState(false);
  const [expandedItems, setExpandedItems] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [expandedHistoryIngredients, setExpandedHistoryIngredients] = useState({});

  // Delivery modal state
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState(null); // null | "email" | "sms"
  const [checkoutStep, setCheckoutStep] = useState("methods"); // "methods" | "retailer"
  const [deliveryTo, setDeliveryTo] = useState("");
  const [deliverySending, setDeliverySending] = useState(false);
  const [deliveryError, setDeliveryError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [retailerAwaitingConfirmation, setRetailerAwaitingConfirmation] = useState(false);
  const [retailerSentCount, setRetailerSentCount] = useState(0);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/cart/");
      setCart(res.data);
    } catch (err) {
      console.error("Failed to fetch cart:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Start with all ingredient sections collapsed
  useEffect(() => {
    if (cart?.items?.length > 0 && expandedItems === null) {
      setExpandedItems({});
    }
  }, [cart, expandedItems]);

  const handleRemoveItem = async (recipeId) => {
    try {
      await axiosInstance.delete(`/api/cart/recipes/${recipeId}/remove/`);
      setCart((prev) => ({
        ...prev,
        items: prev.items.filter((item) => item.recipe.id !== recipeId),
      }));
      setShowShoppingList(false);
      setShoppingList(null);
      window.dispatchEvent(new Event("cart-changed"));
    } catch (err) {
      console.error("Failed to remove item:", err);
      toast.error("Failed to remove recipe from cart.");
    }
  };

  const handleClearCart = async () => {
    try {
      await axiosInstance.delete("/api/cart/clear/");
      setCart((prev) => ({ ...prev, items: [] }));
      setShowShoppingList(false);
      setShoppingList(null);
      window.dispatchEvent(new Event("cart-changed"));
    } catch (err) {
      console.error("Failed to clear cart:", err);
      toast.error("Failed to clear cart.");
    }
  };

  const toggleIngredient = async (itemId, ingredient, currentExcluded) => {
    if (ingredient.is_auto_checked && !ingredient.is_manual_checked) {
      toast.info("This ingredient is already covered by your pantry.", 2500, { important: true });
      return;
    }

    const isExcluded = currentExcluded.includes(ingredient.original_text);
    const newExcluded = isExcluded
      ? currentExcluded.filter((t) => t !== ingredient.original_text)
      : [...currentExcluded, ingredient.original_text];

    setCart((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === itemId ? { ...item, excluded_ingredients: newExcluded } : item
      ),
    }));

    try {
      await axiosInstance.patch(`/api/cart/items/${itemId}/excluded/`, {
        excluded_ingredients: newExcluded,
      });
    } catch (err) {
      console.error("Failed to update excluded:", err);
      toast.error("Failed to update ingredient selection.");
      setCart((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.id === itemId ? { ...item, excluded_ingredients: currentExcluded } : item
        ),
      }));
    }

    if (showShoppingList) {
      fetchShoppingList();
    }
  };

  const fetchShoppingList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await axiosInstance.get("/api/cart/shopping-list/");
      setShoppingList(res.data.items);
      setShowShoppingList(true);
    } catch (err) {
      console.error("Failed to fetch shopping list:", err);
      toast.error("Failed to build shopping list.");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("retailer") !== "connected") return;

    const restoreRetailerCheckout = async () => {
      await fetchShoppingList();
      setShowDeliveryModal(true);
      setCheckoutStep("retailer");
      setDeliveryMethod(null);
      setDeliveryTo("");
      setDeliverySending(false);
      setDeliveryError("");
      navigate("/cart", { replace: true });
    };

    restoreRetailerCheckout();
  }, [fetchShoppingList, location.search, navigate]);

  // Complete checkout opens the delivery modal
  const openDeliveryModal = () => {
    setShowDeliveryModal(true);
    setCheckoutStep("methods");
    setDeliveryMethod(null);
    setDeliveryTo("");
    setDeliverySending(false);
    setDeliveryError("");
    setRetailerAwaitingConfirmation(false);
    setRetailerSentCount(0);
  };

  // Send to shopping list, then checkout
  const handleSendToShoppingList = async () => {
    setCheckoutLoading(true);
    try {
      await axiosInstance.post("/api/cart/send-to-shopping-list/");
      toast.success("Ingredients sent to shopping list.");
      await doCheckout("Ingredients added to your Shopping List. Go to My Pantry to check them off after shopping.", { skipPantry: true });
    } catch (err) {
      console.error("Failed to send to shopping list:", err);
      toast.error("Failed to send ingredients to shopping list.");
      setCheckoutLoading(false);
    }
  };

  // Send via email or sms, then checkout
  const handleSendAndCheckout = async () => {
    if (!deliveryTo.trim()) return;
    setDeliverySending(true);
    try {
      await axiosInstance.post("/api/cart/send-list/", {
        method: deliveryMethod,
        to: deliveryTo.trim(),
      });
      const label = deliveryMethod === "email" ? "emailed" : "texted";
      await doCheckout(`Your shopping list has been ${label} to ${deliveryTo.trim()}. You can view it in Order History anytime.`);
    } catch (err) {
      setDeliverySending(false);
      setDeliveryError("Failed to send. Please try again.");
      toast.error("Failed to send shopping list.");
    }
  };

  // Actual checkout API call
  const doCheckout = async (message, { skipPantry = false } = {}) => {
    setCheckoutLoading(true);
    try {
      await axiosInstance.post("/api/cart/checkout/", { skip_pantry: skipPantry });
      setShowDeliveryModal(false);
      setShowShoppingList(false);
      setShoppingList(null);
      setRetailerAwaitingConfirmation(false);
      setRetailerSentCount(0);
      setSuccessMessage(message);
      setShowCheckoutSuccess(true);
      toast.success("Checkout complete.");
      window.dispatchEvent(new Event("cart-changed"));
      fetchCart();
    } catch (err) {
      console.error("Failed to checkout:", err);
      toast.error("Failed to complete checkout.");
    } finally {
      setCheckoutLoading(false);
      setDeliverySending(false);
    }
  };

  const handleRetailerCheckoutSuccess = async (payload) => {
    const sentCount = payload?.sent || 0;
    setRetailerSentCount(sentCount);
    setRetailerAwaitingConfirmation(true);
    toast.success(`Added ${sentCount} items to Kroger cart. Finish checkout in the Kroger tab, then confirm here.`);
  };

  const handleConfirmRetailerOrderPlaced = async () => {
    await doCheckout(
      `Your Kroger order was confirmed. We recorded it in CartNom history and added those ingredients to your pantry.`,
      { skipPantry: false }
    );
  };

  const fetchHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await axiosInstance.get("/api/cart/history/", {
        params: { page },
      });
      setHistory(res.data.results || []);
      setHistoryHasNext(!!res.data.next);
      setHistoryPage(page);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "history") {
      fetchHistory(1);
    }
  }, [tab, fetchHistory]);

  const toggleExpand = useCallback((itemId) => {
    setExpandedItems((prev) => ({ ...(prev || {}), [itemId]: !(prev || {})[itemId] }));
  }, []);

  const toggleHistoryExpand = useCallback((cartId) => {
    setExpandedHistory((prev) => ({ ...prev, [cartId]: !prev[cartId] }));
  }, []);

  const cartItemCount = cart?.items?.length || 0;

  return (
    <div className="max-w-3xl mx-auto p-6 pb-12">
      {/* Tab Bar */}
      <div className="flex border-b dark:border-gray-700 mb-6">
        <button
          onClick={() => setTab("cart")}
          className={`px-6 py-3 text-sm font-medium transition ${
            tab === "cart"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Current Cart {cartItemCount > 0 && `(${cartItemCount})`}
        </button>
        <button
          onClick={() => setTab("history")}
          className={`px-6 py-3 text-sm font-medium transition ${
            tab === "history"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Order History
        </button>
      </div>

      {/* ─── Current Cart Tab ─── */}
      {tab === "cart" && (
        <>
          {loading ? (
            <div className="text-center py-12 type-muted">Loading cart...</div>
          ) : !cart || cartItemCount === 0 ? (
            <div className="text-center py-16">
              <FaShoppingBasket className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
              <h2 className="type-section-title mb-2">
                Your cart is empty
              </h2>
              <p className="type-muted mb-6">
                Add recipes you want to cook and we'll help you build a shopping list.
              </p>
              <Link
                to="/recipes"
                className="type-button inline-block px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                Browse Recipes
              </Link>
            </div>
          ) : (
            <>
              {/* Cart Header */}
              <div className="flex items-center justify-between mb-4">
                <h1 className="type-section-title">
                  {cartItemCount} {cartItemCount === 1 ? "Recipe" : "Recipes"} in Cart
                </h1>
                <button
                  onClick={handleClearCart}
                  className="text-sm text-red-500 hover:text-red-600 transition"
                >
                  Clear All
                </button>
              </div>

              {/* Step hint */}
              <p className="type-muted mb-4">
                Check off ingredients you already have, then generate your shopping list below.
              </p>

              {/* Recipe Cards with Ingredients */}
              <div className="space-y-4 mb-8">
                {cart.items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl overflow-hidden"
                  >
                    {/* Recipe Header */}
                    <div className="flex items-center p-4">
                      <Link to={`/recipes/${item.recipe.id}`} className="flex-shrink-0">
                        {getRecipeThumbnail(item.recipe) ? (
                          <img
                            src={getRecipeThumbnail(item.recipe)}
                            alt={item.recipe.title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 text-xs">
                            No img
                          </div>
                        )}
                      </Link>
                      <div className="ml-4 flex-1 min-w-0">
                        <Link
                          to={`/recipes/${item.recipe.id}`}
                          className="font-semibold text-gray-800 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition truncate block"
                        >
                          {item.recipe.title}
                          {item.quantity > 1 && (
                            <span className="ml-2 text-sm font-bold text-blue-500 dark:text-blue-400">x{item.quantity}</span>
                          )}
                        </Link>
                        <p className="text-sm text-gray-400 mt-0.5">
                          {item.recipe.servings && `${item.recipe.servings} servings`}
                          {item.recipe.servings && item.recipe.cook_time && " · "}
                          {item.recipe.cook_time && `${item.recipe.cook_time} min`}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(item.recipe.id)}
                        className="ml-3 text-gray-400 hover:text-red-500 transition p-2"
                        title="Remove from cart"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>

                    {/* Expandable Ingredients */}
                    <div className="border-t dark:border-gray-700">
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                      >
                        <span>
                          {item.recipe.recipe_ingredients?.length || 0} ingredients
                          {item.excluded_ingredients?.length > 0 && (
                            <span className="text-green-500 ml-2">
                              ({item.excluded_ingredients.length} checked off)
                            </span>
                          )}
                        </span>
                        {(expandedItems || {})[item.id] ? (
                          <FaChevronUp size={12} />
                        ) : (
                          <FaChevronDown size={12} />
                        )}
                      </button>
                      {(expandedItems || {})[item.id] && (
                        <div className="px-4 pb-4 space-y-2">
                          <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                            Check off ingredients you already have:
                          </p>
                          {(item.ingredient_statuses?.length ? item.ingredient_statuses : item.recipe.recipe_ingredients || []).map((ing) => {
                            const isExcluded = typeof ing.is_checked === "boolean"
                              ? ing.is_checked
                              : (item.excluded_ingredients || []).includes(ing.original_text);
                            return (
                              <label
                                key={ing.id}
                                className={`flex items-start space-x-3 group ${ing.is_auto_checked ? "cursor-default" : "cursor-pointer"}`}
                                onClick={() =>
                                  toggleIngredient(
                                    item.id,
                                    ing,
                                    item.excluded_ingredients || []
                                  )
                                }
                              >
                                <div
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
                                    isExcluded
                                      ? "bg-green-500 border-green-500"
                                      : "border-gray-300 dark:border-gray-600 group-hover:border-green-300"
                                  }`}
                                >
                                  {isExcluded && (
                                    <FaCheck className="text-white" size={10} />
                                  )}
                                </div>
                                <div>
                                  <span
                                    className={`text-sm transition ${
                                      isExcluded
                                        ? "text-gray-400 line-through"
                                        : "text-gray-700 dark:text-gray-300"
                                    }`}
                                  >
                                    {ing.display_text || ing.original_text}
                                  </span>
                                  {ing.pantry_note && (
                                    <span className="block text-xs text-green-500 dark:text-green-400 mt-0.5">
                                      {ing.pantry_note}
                                    </span>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Generate Shopping List Button */}
              {!showShoppingList && (
                <button
                  onClick={fetchShoppingList}
                  disabled={listLoading}
                  className="type-button w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {listLoading ? "Generating..." : "Generate Shopping List"}
                </button>
              )}

              {/* Shopping List (clean view, no send buttons) */}
              {showShoppingList && shoppingList && (
                <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl p-6 mt-6">
                  <h2 className="type-section-title mb-4">
                    Shopping List
                  </h2>

                  {shoppingList.length === 0 ? (
                    <p className="type-muted text-center py-4">
                      All ingredients checked off! Nothing to buy.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {shoppingList.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-start justify-between py-2 border-b dark:border-gray-700 last:border-b-0"
                        >
                          <div>
                            <span className="text-gray-800 dark:text-white font-medium">
                              {item.quantity != null && (
                                <span className="text-blue-600 dark:text-blue-400 font-semibold mr-1">
                                  {item.quantity}
                                </span>
                              )}
                              {item.unit && (
                                <span className="text-gray-500 dark:text-gray-400 mr-1">
                                  {item.unit}
                                </span>
                              )}
                              {item.name}
                            </span>
                            {item.from_recipes?.length > 0 && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                from: {item.from_recipes.join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={() => {
                        setShowShoppingList(false);
                        setShoppingList(null);
                      }}
                      className="flex-1 py-3 border dark:border-gray-600 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      Back to Cart
                    </button>
                    <button
                      onClick={openDeliveryModal}
                      disabled={checkoutLoading}
                      className="flex-1 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition disabled:opacity-50"
                    >
                      {checkoutLoading ? "Processing..." : "Complete Checkout"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ─── Order History Tab ─── */}
      {tab === "history" && (
        <>
          {historyLoading ? (
            <div className="text-center py-12 type-muted">Loading history...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-16">
              <FaShoppingBasket className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
              <h2 className="type-section-title mb-2">
                No orders yet
              </h2>
              <p className="type-muted">
                Completed checkouts will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((order) => (
                <div
                  key={order.id}
                  className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleHistoryExpand(order.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                  >
                    <div className="text-left">
                      <p className="font-medium text-gray-800 dark:text-white">
                        {order.items?.length || 0}{" "}
                        {(order.items?.length || 0) === 1 ? "recipe" : "recipes"}
                      </p>
                      <p className="text-sm text-gray-400 mt-0.5">
                        {order.checked_out_at
                          ? new Date(order.checked_out_at).toLocaleDateString("en-US", {
                              weekday: "short",
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "Unknown date"}
                      </p>
                    </div>
                    {expandedHistory[order.id] ? (
                      <FaChevronUp className="text-gray-400" size={12} />
                    ) : (
                      <FaChevronDown className="text-gray-400" size={12} />
                    )}
                  </button>
                  {expandedHistory[order.id] && (
                    <div className="border-t dark:border-gray-700 p-4 space-y-2">
                      {order.items?.map((item) => {
                        const ingKey = `${order.id}-${item.id}`;
                        const ingExpanded = expandedHistoryIngredients[ingKey];
                        return (
                          <div
                            key={item.id}
                            className="bg-gray-50 dark:bg-gray-700/40 rounded-lg overflow-hidden"
                          >
                            <button
                              onClick={() =>
                                setExpandedHistoryIngredients((prev) => ({
                                  ...prev,
                                  [ingKey]: !prev[ingKey],
                                }))
                              }
                              className="w-full flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                            >
                              {item.snapshot_image_url ? (
                                <img
                                  src={toAbsoluteUrl(item.snapshot_image_url)}
                                  alt={item.snapshot_title}
                                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-400 text-xs flex-shrink-0">
                                  No img
                                </div>
                              )}
                              <div className="ml-3 flex-1 text-left min-w-0">
                                {item.snapshot_meal_plan_day != null && (
                                  <p className="flex items-center gap-1 text-[11px] text-blue-500 dark:text-blue-400 font-medium mb-0.5">
                                    <FaCalendarAlt size={9} />
                                    {MEAL_PLAN_DAYS[item.snapshot_meal_plan_day]}
                                    {item.snapshot_meal_plan_meal && ` · ${item.snapshot_meal_plan_meal.charAt(0).toUpperCase() + item.snapshot_meal_plan_meal.slice(1)}`}
                                  </p>
                                )}
                                <p className="font-medium text-gray-800 dark:text-white text-sm truncate">
                                  {item.snapshot_title || "Untitled"}
                                  {item.snapshot_quantity > 1 && (
                                    <span className="ml-2 text-sm font-bold text-blue-500 dark:text-blue-400">x{item.snapshot_quantity}</span>
                                  )}
                                </p>
                                {item.snapshot_ingredients?.length > 0 && (
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {item.snapshot_ingredients.length} ingredients
                                  </p>
                                )}
                              </div>
                              {item.snapshot_ingredients?.length > 0 && (
                                ingExpanded ? (
                                  <FaChevronUp className="text-gray-400 flex-shrink-0" size={10} />
                                ) : (
                                  <FaChevronDown className="text-gray-400 flex-shrink-0" size={10} />
                                )
                              )}
                            </button>
                            {ingExpanded && item.snapshot_ingredients?.length > 0 && (
                              <div className="px-3 pb-3 pt-1 border-t dark:border-gray-600">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                  {item.snapshot_ingredients.map((ing, idx) => (
                                    <p key={idx} className="text-xs text-gray-600 dark:text-gray-300 py-0.5">
                                      <span className="text-gray-400 mr-1">•</span>
                                      {ing.original_text || `${ing.quantity || ''} ${ing.unit || ''} ${ing.name || ''}`.trim()}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {(!order.items || order.items.length === 0) && (
                        <p className="text-sm text-gray-400 italic">
                          Items no longer available (recipe was deleted).
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Pagination */}
              {(historyHasNext || historyPage > 1) && (
                <div className="flex justify-center space-x-4 mt-6">
                  {historyPage > 1 && (
                    <button
                      onClick={() => fetchHistory(historyPage - 1)}
                      className="px-4 py-2 text-sm border dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      Previous
                    </button>
                  )}
                  {historyHasNext && (
                    <button
                      onClick={() => fetchHistory(historyPage + 1)}
                      className="px-4 py-2 text-sm border dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      Next
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ─── Delivery Method Modal ─── */}
      {showDeliveryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full overflow-hidden ${checkoutStep === "retailer" ? "max-w-5xl" : "max-w-md"}`}>
            <div className={`p-6 ${checkoutStep === "retailer" ? "max-h-[85vh] overflow-y-auto" : ""}`}>
              <h2 className="type-section-title mb-2">
                {checkoutStep === "retailer" ? "Shop with a Retailer" : "Send Your Shopping List"}
              </h2>
              <p className="type-muted mb-6">
                {checkoutStep === "retailer"
                  ? "Use the exact filtered checkout list below to build a retailer-ready cart."
                  : "How would you like to receive your shopping list?"}
              </p>

              {/* Method buttons (shown when no method selected) */}
              {!deliveryMethod && checkoutStep === "methods" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setDeliveryMethod("shopping-list")}
                      className="flex flex-col items-center justify-center gap-2 px-3 py-4 border dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <FaShoppingBasket className="text-gray-600 dark:text-gray-300" size={16} />
                      </div>
                      <span className="text-xs font-medium text-gray-800 dark:text-white text-center">Shopping List</span>
                    </button>

                    <button
                      onClick={() => setDeliveryMethod("email")}
                      className="flex flex-col items-center justify-center gap-2 px-3 py-4 border dark:border-gray-600 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                        <FaEnvelope className="text-blue-600 dark:text-blue-400" size={16} />
                      </div>
                      <span className="text-xs font-medium text-gray-800 dark:text-white text-center">Email</span>
                    </button>

                    <button
                      onClick={() => setDeliveryMethod("sms")}
                      className="flex flex-col items-center justify-center gap-2 px-3 py-4 border dark:border-gray-600 rounded-xl hover:bg-green-50 dark:hover:bg-green-900/20 transition"
                    >
                      <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                        <FaSms className="text-green-600 dark:text-green-400" size={16} />
                      </div>
                      <span className="text-xs font-medium text-gray-800 dark:text-white text-center">Text</span>
                    </button>
                  </div>

                  <button
                    onClick={() => setCheckoutStep("retailer")}
                    className="w-full py-3 bg-indigo-500 text-white rounded-xl font-medium text-sm hover:bg-indigo-600 transition"
                  >
                    Shop with a Retailer
                  </button>
                </div>
              )}

              {checkoutStep === "retailer" && !deliveryMethod && !retailerAwaitingConfirmation && (
                <div className="space-y-4">
                  <button
                    onClick={() => setCheckoutStep("methods")}
                    className="text-sm text-blue-500 hover:text-blue-600 transition"
                  >
                    Back
                  </button>
                  <RetailerReviewPanel
                    items={shoppingList || []}
                    compact
                    source="cart"
                    onCartSendSuccess={handleRetailerCheckoutSuccess}
                  />
                </div>
              )}

              {checkoutStep === "retailer" && !deliveryMethod && retailerAwaitingConfirmation && (
                <div className="space-y-5">
                  <button
                    onClick={() => setRetailerAwaitingConfirmation(false)}
                    className="text-sm text-blue-500 hover:text-blue-600 transition"
                  >
                    Back to Kroger review
                  </button>

                  <div className="rounded-2xl border dark:border-gray-600 p-5 space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 dark:text-white">
                        Finish checkout on Kroger
                      </h3>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        We added {retailerSentCount} item{retailerSentCount === 1 ? "" : "s"} to your Kroger cart.
                        Complete the order on Kroger, then come back here and confirm it so CartNom can record this checkout.
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button
                        onClick={() => window.open(KROGER_CART_URL, "_blank")}
                        className="flex-1 py-3 border dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                      >
                        Open Kroger Cart Again
                      </button>
                      <button
                        onClick={handleConfirmRetailerOrderPlaced}
                        disabled={checkoutLoading}
                        className="flex-1 py-3 bg-green-500 text-white rounded-xl font-medium text-sm hover:bg-green-600 transition disabled:opacity-50"
                      >
                        {checkoutLoading ? "Saving..." : "I Placed My Kroger Order"}
                      </button>
                    </div>

                    <button
                      onClick={() => setShowDeliveryModal(false)}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                    >
                      I’ll finish this later
                    </button>
                  </div>
                </div>
              )}

              {/* Shopping List view */}
              {deliveryMethod === "shopping-list" && (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-3">
                      <FaShoppingBasket className="text-gray-600 dark:text-gray-300" size={20} />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Ingredients will be added to your Shopping List in My Pantry. Check them off after shopping to move them to your pantry.
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setDeliveryMethod(null)}
                      className="flex-1 py-3 border dark:border-gray-600 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSendToShoppingList}
                      disabled={checkoutLoading}
                      className="flex-1 py-3 bg-green-500 text-white rounded-xl font-medium text-sm hover:bg-green-600 transition disabled:opacity-50"
                    >
                      {checkoutLoading ? "Processing..." : "Send & Checkout"}
                    </button>
                  </div>
                </div>
              )}

              {/* Email input */}
              {deliveryMethod === "email" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email address
                    </label>
                    <input
                      type="email"
                      placeholder="name@example.com"
                      value={deliveryTo}
                      onChange={(e) => { setDeliveryTo(e.target.value); setDeliveryError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && handleSendAndCheckout()}
                      autoFocus
                      className="w-full px-4 py-3 text-sm border dark:border-gray-600 rounded-xl outline-none focus:ring-2 focus:ring-blue-300 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                    />
                    {deliveryError && (
                      <p className="text-red-500 text-xs mt-1">{deliveryError}</p>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => { setDeliveryMethod(null); setDeliveryTo(""); setDeliveryError(""); }}
                      className="flex-1 py-3 border dark:border-gray-600 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSendAndCheckout}
                      disabled={deliverySending || !deliveryTo.trim()}
                      className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-medium text-sm hover:bg-blue-600 transition disabled:opacity-50"
                    >
                      {deliverySending ? "Sending..." : "Send & Checkout"}
                    </button>
                  </div>
                </div>
              )}

              {/* SMS input */}
              {deliveryMethod === "sms" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone number
                    </label>
                    <input
                      type="tel"
                      placeholder="+15551234567"
                      value={deliveryTo}
                      onChange={(e) => { setDeliveryTo(e.target.value); setDeliveryError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && handleSendAndCheckout()}
                      autoFocus
                      className="w-full px-4 py-3 text-sm border dark:border-gray-600 rounded-xl outline-none focus:ring-2 focus:ring-green-300 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                    />
                    {deliveryError && (
                      <p className="text-red-500 text-xs mt-1">{deliveryError}</p>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => { setDeliveryMethod(null); setDeliveryTo(""); setDeliveryError(""); }}
                      className="flex-1 py-3 border dark:border-gray-600 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSendAndCheckout}
                      disabled={deliverySending || !deliveryTo.trim()}
                      className="flex-1 py-3 bg-green-500 text-white rounded-xl font-medium text-sm hover:bg-green-600 transition disabled:opacity-50"
                    >
                      {deliverySending ? "Sending..." : "Send & Checkout"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Cancel */}
            {!deliverySending && (
              <button
                onClick={() => setShowDeliveryModal(false)}
                className="w-full py-3 border-t dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Checkout Success Modal */}
      {showCheckoutSuccess && (
        <ConfirmModal
          title="Checkout Complete!"
          message={successMessage}
          confirmText="View History"
          cancelText="Browse Recipes"
          variant="success"
          onConfirm={() => {
            setShowCheckoutSuccess(false);
            setTab("history");
            fetchHistory(1);
          }}
          onCancel={() => {
            setShowCheckoutSuccess(false);
            navigate("/recipes?view=public");
          }}
        />
      )}
    </div>
  );
}
